
import React, { useState, useEffect } from 'react';
import { Menu, X, Utensils, Globe } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from './LanguageContext';

const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [logoError, setLogoError] = useState(false); // State to track logo loading error
  const location = useLocation();
  const { t, toggleLanguage, language } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      // Trigger almost immediately on scroll (10px)
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Determine if we are on the home page
  const isHome = location.pathname === '/';
  
  // Navbar background logic - Updated for Floating Effect
  const navContainerClasses = scrolled
    ? 'top-4 left-0 right-0 w-[95%] max-w-7xl mx-auto rounded-full bg-fait-dark/95 backdrop-blur-md shadow-[0_8px_30px_rgb(0,0,0,0.5)] border border-fait-gold/20 py-2'
    : 'top-0 w-full bg-transparent border-b border-transparent py-6';

  const navLinks = [
    { name: t.nav.home, href: '/' },
    { name: t.nav.story, href: '/story' },
    { name: t.nav.menu, href: '/menu' },
    { name: t.nav.gallery, href: '/gallery' },
    { name: t.nav.faq, href: '/faq' },
    { name: t.nav.contact, href: '/contact' },
  ];

  return (
    <nav className={`fixed z-50 transition-all duration-500 ease-in-out ${navContainerClasses}`}>
      <div className="px-4 sm:px-8 w-full">
        <div className="flex items-center justify-between h-14">
          
          {/* Logo */}
          <Link to="/" className="flex-shrink-0 flex items-center gap-2 group">
            {!logoError ? (
              <img 
                src="/images/logo.svg" 
                alt={language === 'he' ? 'לוגו יהודה פייט' : 'Yehuda Fait Logo'}
                className="h-12 w-auto object-contain transition-transform duration-300 group-hover:scale-105"
                onError={() => setLogoError(true)}
              />
            ) : (
              <>
                <Utensils className="h-6 w-6 md:h-8 md:w-8 text-fait-gold group-hover:rotate-12 transition-transform duration-300" />
                <span className="font-serif text-xl md:text-2xl font-bold text-fait-cream tracking-wide group-hover:text-fait-gold transition-colors">
                  {language === 'he' ? 'יהודה פייט' : 'Yehuda Fait'}
                </span>
              </>
            )}
          </Link>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center">
            <div className="ml-10 flex items-baseline space-x-1 space-x-reverse rtl:space-x-reverse ltr:space-x-1">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`px-3 py-2 rounded-full text-base font-medium transition-all duration-300 font-serif ${
                    location.pathname === link.href 
                        ? 'text-fait-gold font-bold' 
                        : 'text-fait-cream hover:text-fait-gold hover:bg-white/5'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
            </div>
            
            <div className="h-6 w-px bg-white/10 mx-4"></div>

            {/* Language Toggle Desktop */}
            <button 
                onClick={toggleLanguage}
                className="flex items-center gap-1 px-3 py-1 rounded-full border border-fait-gold/30 text-fait-gold hover:bg-fait-gold/10 transition-colors text-sm font-sans"
            >
                <Globe size={16} />
                <span>{language === 'he' ? 'EN' : 'עב'}</span>
            </button>
          </div>

          {/* Mobile menu button */}
          <div className="-mr-2 flex lg:hidden items-center gap-2">
            <button 
                onClick={toggleLanguage}
                className="flex items-center gap-1 px-2 py-1 rounded-full border border-fait-gold/30 text-fait-gold hover:bg-fait-gold/10 transition-colors text-xs font-sans"
            >
                <Globe size={14} />
                <span>{language === 'he' ? 'EN' : 'עב'}</span>
            </button>

            <button
              onClick={() => setIsOpen(!isOpen)}
              type="button"
              className="inline-flex items-center justify-center p-2 rounded-md text-fait-gold hover:text-white focus:outline-none relative w-10 h-10"
              aria-controls="mobile-menu"
              aria-expanded="false"
            >
              <span className="sr-only">Open main menu</span>
              
              {/* Animated Hamburger/X Icon */}
              <div className="relative w-6 h-6">
                <span className={`absolute inset-0 transform transition-all duration-300 ease-in-out origin-center ${isOpen ? 'opacity-0 rotate-90 scale-50' : 'opacity-100 rotate-0 scale-100'}`}>
                   <Menu className="w-6 h-6" />
                </span>
                <span className={`absolute inset-0 transform transition-all duration-300 ease-in-out origin-center ${isOpen ? 'opacity-100 rotate-0 scale-100' : 'opacity-0 -rotate-90 scale-50'}`}>
                   <X className="w-6 h-6" />
                </span>
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <div className={`lg:hidden absolute top-full left-0 right-0 mt-2 mx-4 rounded-2xl bg-fait-dark/95 border border-fait-gold/20 backdrop-blur-md overflow-hidden transition-all duration-300 ease-in-out shadow-2xl ${isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`} id="mobile-menu">
        <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
          {navLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              onClick={() => setIsOpen(false)}
              className={`block px-3 py-2 rounded-xl text-base font-medium text-center font-serif ${
                 location.pathname === link.href ? 'text-fait-gold font-bold bg-white/5' : 'text-fait-cream hover:text-fait-gold hover:bg-white/5'
              }`}
            >
              {link.name}
            </Link>
          ))}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
